import { FfsResultType } from 'features/indicationTriage/cards/ffsCalc/ffsResultPublish/ffsTypes'

export interface FfsAnalysisResult {
  resultId: string
  resultType: FfsResultType
  resultLabel: string
  condition: string
  explanation: string
  failedCriteria?: string[]
}

export enum FfsPartFourResultIds {
  PASS = 'pass',
  FAIL_MAWP_CRITERIA = 'fail-mawp-criteria',
  FAIL_THICKNESS_CRITERIA = 'fail-thickness-criteria',
  FAIL_MISSING_INFORMATION = 'fail-missing-information',
}

export enum FfsPartFiveResultIds {
  PASS_HIGH_RSF = 'pass-high-rsf',
  PASS_LOW_RSF = 'pass-low-rsf',
  DERATE = 'derate',
  FAIL_FLAW_SIZE = 'fail-flaw-size',
  FAIL_MISSING_DATA = 'fail-missing-data',
}

export const INDETERMINATE_FFS_RESULT_ID = 'indeterminate'

export const DEFAULT_INDETERMINATE_RESULT: FfsAnalysisResult = {
  resultId: INDETERMINATE_FFS_RESULT_ID,
  resultType: FfsResultType.Fail,
  resultLabel: 'Indeterminate',
  condition: 'Indeterminate conditions',
  explanation: 'Unable to determine result based on available data',
}

export const FFS_RESULT_CASES_PART_4: readonly FfsAnalysisResult[] = [
  // pass
  {
    resultId: FfsPartFourResultIds.PASS,
    resultType: FfsResultType.Pass,
    resultLabel: 'Pass',
    condition: 'MAWPr >= Design MAWP',
    explanation:
      'Equipment OK for continued operation at original design pressure/MAWP',
  },
  // derate mawp
  {
    resultId: FfsPartFourResultIds.FAIL_MAWP_CRITERIA,
    resultType: FfsResultType.Derate,
    resultLabel: 'Derate',
    condition: 'MAWP criteria failing',
    explanation: 'Derate to MAWPr due to insufficient remaining strength',
  },
  // fail thickness criteria
  {
    resultId: FfsPartFourResultIds.FAIL_THICKNESS_CRITERIA,
    resultType: FfsResultType.Fail,
    resultLabel: 'Fail',
    condition: 'Thickness criteria failing',
    explanation:
      'Analysis failed API-579 Part 4 Step 5; thickness criteria, calculation invalid',
  },
  //fail-missing information
  {
    resultId: FfsPartFourResultIds.FAIL_MISSING_INFORMATION,
    resultType: FfsResultType.Fail,
    resultLabel: 'Fail',
    condition: 'Missing information',
    explanation: 'Cannot determine result: missing information',
  },
  // indeterminate
  {
    resultId: INDETERMINATE_FFS_RESULT_ID,
    resultType: FfsResultType.Fail,
    resultLabel: 'Indeterminate',
    condition: 'Indeterminate conditions',
    explanation: 'Unable to determine result based on available data',
  },
]

/**
 * Centralized FFS result cases based on API-579 Part 5 criteria.
 *
 * Level 1: Uses MAWPr vs MAWP_design comparison
 * Level 2 (STEP 9): Uses RSF vs RSF_a comparison (Critical Thickness Profile)
 *   - If RSF >= RSF_a: acceptable for operation at MAWP from STEP 7
 *   - If RSF < RSF_a: derate to MAWPr (computed using Part 2, paragraph 2.4.2.2)
 */
export const FFS_RESULT_CASES_PART_5: readonly FfsAnalysisResult[] = [
  {
    resultId: FfsPartFiveResultIds.PASS_HIGH_RSF,
    resultType: FfsResultType.Pass,
    resultLabel: 'Pass',
    condition: 'MAWPr > Design MAWP & RSF >= 0.9',
    explanation:
      'Equipment OK for continued operation at original design pressure/MAWP',
  },
  {
    resultId: FfsPartFiveResultIds.PASS_LOW_RSF,
    resultType: FfsResultType.Pass,
    resultLabel: 'Pass',
    condition: 'MAWPr > Design MAWP & RSF < 0.9',
    explanation:
      'Equipment OK for continued operation at original design pressure. Remaining strength factor is less than or equal to 0.9 but strength is greater than required for stress due to design pressure.',
  },
  {
    resultId: FfsPartFiveResultIds.DERATE,
    resultType: FfsResultType.Derate,
    resultLabel: 'Derate',
    condition: 'Level 1: MAWPr < Design MAWP | Level 2: RSF < RSF_a',
    explanation: 'Derate to MAWPr due to insufficient remaining strength',
  },
  {
    resultId: FfsPartFiveResultIds.FAIL_FLAW_SIZE,
    resultType: FfsResultType.Fail,
    resultLabel: 'Fail',
    condition: 'Flaw size criteria failing',
    explanation:
      'Analysis failed API-579 Part 5 Step 5; remaining thickness, ratio, and/or location criteria, calculation invalid',
  },
  {
    resultId: FfsPartFiveResultIds.FAIL_MISSING_DATA,
    resultType: FfsResultType.Fail,
    resultLabel: 'Fail',
    condition:
      'Level 1: Missing Design MAWP or MAWPr | Level 2: Missing RSF or RSF_a',
    explanation:
      'Cannot determine result: missing required values for acceptability check',
  },
  {
    resultId: INDETERMINATE_FFS_RESULT_ID,
    resultType: FfsResultType.Fail,
    resultLabel: 'Indeterminate',
    condition: 'Indeterminate conditions',
    explanation: 'Unable to determine result based on available data',
  },
]
